import { type FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button } from 'react-bootstrap'
import classNames from 'classnames'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../../store/auth/authSlice'
import { useGetAllUsersQuery, useGetUserByIdQuery } from '../../../store/user/user'
import { isAdminOrEnterpriseAdmin, isOwner } from '../../../common/userUtils'
import styles from './UserScoreDetailsPage.module.scss'
import { UserScoreDetails } from '../../../components/Insights/UserScoreDetails/UserScoreDetails'
import { RANGE_PERIOD_TEXT, type RangeKey } from '../../../components/Insights/userScoreRange'
import {
  ALL_ROSTER_GROUP_KEYS,
  buildScoreRows,
  filterRowsByQuery,
  filterVisibleRoster,
  groupFullRoster,
  orderGroupedRoster,
  type RosterGroupKey,
  type ScoreRow,
} from '../../../components/Insights/scoreRoster'
import { useUserScoresGroupedData } from '../../../components/Insights/useUserScoresData'
import { UserScorePager } from './UserScorePager'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

// Turns true a beat after the first load finishes, then stays true
const usePagerVisible = (loading: boolean): boolean => {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (loading || visible) {
      return
    }
    const timer = window.setTimeout(() => setVisible(true), 1000)
    return () => window.clearTimeout(timer)
  }, [loading, visible])

  return visible
}

export const UserScoreDetailsPage: FC = () => {
  const navigate = useNavigate()
  const { userId = '' } = useParams()
  const accountId = useAppSelector(selectAccountId)
  const authenticatedUser = useAppSelector(selectAuthenticatedUser)
  const userQueryEnabled = Boolean(accountId) && Boolean(userId)
  const {
    data: userResponse,
    isFetching: isFetchingUser,
    isError: isUserError,
  } = useGetUserByIdQuery({ accountId: accountId ?? '', userId }, { skip: !userQueryEnabled })
  const userName = userResponse?.data?.name?.trim() ?? ''
  // Users without a name are identified by their email; a failed lookup falls back to the generic title
  const userLabel = userName || userResponse?.data?.email?.trim() || 'Meeting score'
  // Only the query state decides what is still loading, and a failed request has finished loading too
  const userNameLoading = isFetchingUser || (userQueryEnabled && !userResponse && !isUserError)
  const firstName = userName.split(' ')[0]

  const [searchParams] = useSearchParams()
  const rangeParam = searchParams.get('range')
  const initialRange: RangeKey = rangeParam && rangeParam in RANGE_PERIOD_TEXT ? (rangeParam as RangeKey) : '90d'
  // Without roster context (direct visit, profile links) there is no queue and no pager
  const hasRosterContext = searchParams.get('view') === 'grouped'
  const query = searchParams.get('q') ?? ''
  const expandedParam = searchParams.get('expanded') ?? ''
  const visibleParam = searchParams.get('visible') ?? ''

  const { trackingClient } = useTracking()
  useEffect(() => {
    // Profile-menu links land here without roster context and must not be tracked
    if (hasRosterContext) {
      trackingClient.track({ eventName: TrackEvent.INSIGHTS_SCORECARD_DETAILS_OPENED, eventDetails: { userId } })
    }
  }, [hasRosterContext, userId, trackingClient])

  // Without roster context the URL keeps its original range, so the switcher is the only source of truth
  const [activeRange, setActiveRange] = useState<RangeKey>(initialRange)
  useEffect(() => {
    setActiveRange(initialRange)
  }, [initialRange])

  const { data: usersResponse } = useGetAllUsersQuery({ accountId: accountId ?? '', identityTypes: ['idp'] }, { skip: !accountId })
  const { scorecards } = useUserScoresGroupedData(accountId ?? '', activeRange)
  const activeUsers = useMemo(() => (usersResponse?.data ?? []).filter((user) => user.status === 'active'), [usersResponse])
  const visibleUsers = useMemo(() => filterVisibleRoster(activeUsers, authenticatedUser), [activeUsers, authenticatedUser])
  // Ranking (top performer) runs on the full roster so it never shifts based on who is viewing
  const fullRosterRows = useMemo(() => buildScoreRows(activeUsers, scorecards), [activeUsers, scorecards])
  const rosterRows = useMemo(() => buildScoreRows(visibleUsers, scorecards), [visibleUsers, scorecards])
  const isTopPerformer = useMemo(() => groupFullRoster(fullRosterRows).topPerformers.some((row) => row.id === userId), [fullRosterRows, userId])
  const queue = useMemo(() => {
    if (!hasRosterContext) {
      return []
    }
    const filtered = filterRowsByQuery(rosterRows, query)
    const isRosterGroupKey = (key: string): key is RosterGroupKey => (ALL_ROSTER_GROUP_KEYS as string[]).includes(key)
    const visibleGroups = new Set(visibleParam.split(',').filter(isRosterGroupKey))
    const visibleCounts = new Map(
      expandedParam
        .split(',')
        .filter(Boolean)
        .map((entry) => entry.split(':'))
        .filter(([key, count]) => isRosterGroupKey(key) && Number.isFinite(Number(count)))
        .map(([key, count]) => [key as RosterGroupKey, Number(count)] as const),
    )
    return orderGroupedRoster(filtered, visibleGroups, visibleCounts, fullRosterRows)
  }, [hasRosterContext, rosterRows, fullRosterRows, query, visibleParam, expandedParam])

  const queueIndex = queue.findIndex((row) => row.id === userId)

  const [detailsLoading, setDetailsLoading] = useState(true)
  const pagerVisible = usePagerVisible(detailsLoading)

  const navigateToRow = useCallback(
    (row: ScoreRow) => {
      navigate(
        `/insights/coaching/${row.id}?range=${activeRange}&view=grouped&visible=${visibleParam}&expanded=${expandedParam}&q=${encodeURIComponent(query)}`,
      )
    },
    [navigate, activeRange, visibleParam, expandedParam, query],
  )

  const pageRef = useRef<HTMLDivElement>(null)

  const { portalRef } = useContext(HeaderContext)
  const [headerContainer, setHeaderContainer] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (portalRef?.current) {
      setHeaderContainer(portalRef.current)
    }
  }, [portalRef])

  // Admins are hidden from non-owners in the roster, so opening one directly by URL must be blocked too.
  // Until the lookup resolves (or if it fails) the target user can't be verified safe, so treat both as hidden.
  // A non-owner admin still sees their own scorecard, mirroring the self-exception in filterVisibleRoster.
  const viewerIsOwner = isOwner(authenticatedUser)
  const viewingSelf = Boolean(authenticatedUser?.id) && userId === authenticatedUser?.id
  const targetLookupPending = userQueryEnabled && !userResponse && !isUserError
  const viewedUserHidden = !viewerIsOwner && !viewingSelf && (targetLookupPending || isUserError || isAdminOrEnterpriseAdmin(userResponse?.data))
  useEffect(() => {
    if (!viewerIsOwner && !viewingSelf && (isUserError || (userResponse?.data && isAdminOrEnterpriseAdmin(userResponse.data)))) {
      navigate('/insights/coaching', { replace: true })
    }
  }, [viewerIsOwner, viewingSelf, isUserError, userResponse, navigate])

  if (viewedUserHidden) {
    return null
  }

  return (
    <div ref={pageRef} className={classNames('truly-full-size-content w-100 zocks-px-2.0 pt-5', styles.pageContainer, styles.detailPageBottomPad)}>
      {headerContainer
        ? createPortal(
            <div className='d-flex align-items-center gap-3 zocks-pl-2.0'>
              <Button
                variant='link'
                className={classNames('zocks-text-gray-900', styles.backButton)}
                aria-label='View Coaching'
                onClick={() => navigate('/insights/coaching')}
              >
                <i className='bi bi-chevron-left' />
              </Button>
              <span className='fw-semibold zocks-text-gray-900'>View Coaching</span>
            </div>,
            headerContainer,
          )
        : null}
      <UserScoreDetails
        key={userId}
        userId={userId}
        heading={
          <h2 className='h2'>
            {userNameLoading ? <span className={classNames('rounded-2', styles.headingSkeleton)} role='status' aria-label='Loading user name' /> : userLabel}
          </h2>
        }
        firstName={firstName}
        subtitlePending={userNameLoading}
        initialRange={initialRange}
        onRangeChange={(range) => {
          setActiveRange(range)
          if (hasRosterContext) {
            navigate(`?range=${range}&view=grouped&visible=${visibleParam}&expanded=${expandedParam}&q=${encodeURIComponent(query)}`, { replace: true })
          }
        }}
        onLoadingChange={setDetailsLoading}
        isTopPerformer={isTopPerformer}
      />
      {hasRosterContext && queueIndex >= 0 && queue.length > 1 ? (
        <UserScorePager
          previous={queueIndex > 0 ? queue[queueIndex - 1] : null}
          next={queueIndex < queue.length - 1 ? queue[queueIndex + 1] : null}
          position={queueIndex + 1}
          total={queue.length}
          hidden={!pagerVisible}
          anchorRef={pageRef}
          onNavigate={navigateToRow}
        />
      ) : null}
    </div>
  )
}
